import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { API_BASE } from '@/lib/api-base'
import {
  Plus, Pencil, Trash2, Phone, MapPin, User,
  Calendar, DollarSign, Link2, Unlink
} from "lucide-react"
import {
  MASK_MAX_LENGTH,
  cepError,
  cpfError,
  maskCep,
  maskCpf,
  maskNumero,
  maskPhone,
  onlyDigits,
  optionalPhoneError,
  phoneError,
} from "@/lib/br-masks"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { SetupModuleEmptyCta } from "@/components/setup-module-empty-cta"

interface Colaborador {
  id: string
  nome: string
  cpf: string
  telefone: string
  telefone_secundario: string | null
  email: string | null
  cargo: string
  departamento: string
  data_admissao: string
  salario: number | null
  cidade: string | null
  estado: string | null
  bairro: string | null
  logradouro: string | null
  numero: string | null
  cep: string | null
  usuario_id: string | null
  usuario_email: string | null
  status: string
  criado_em: string
}

interface Usuario {
  id: string
  email: string
  nome: string
}

const DEPARTAMENTOS = [
  "Producao",
  "Administrativo",
  "Financeiro",
  "Comercial",
  "Manutencao",
  "Qualidade",
  "Outros"
]

const CARGOS_COMUNS = [
  "Gerente",
  "Supervisor",
  "Tecnico Avicola",
  "Operador",
  "Auxiliar",
  "Motorista",
  "Vigia",
  "Servicos Gerais",
  "Outros"
]

const ESTADOS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
]

function ColaboradoresPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([])
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editColaborador, setEditColaborador] = useState<Colaborador | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  
  const [form, setForm] = useState({
    nome: "",
    cpf: "",
    telefone: "",
    telefone_secundario: "",
    email: "",
    cargo: "",
    departamento: "Producao",
    data_admissao: todayISODate(),
    salario: "",
    cidade: "",
    estado: "",
    bairro: "",
    logradouro: "",
    numero: "",
    cep: "",
    usuario_id: "",
  })

  const fetchColaboradores = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/colaboradores/`)
      if (res.ok) {
        setColaboradores(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/admin/usuarios-tenant`)
      if (res.ok) {
        setUsuarios(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchColaboradores()
    void fetchUsuarios()
  }, [user, isSuperAdmin, selectedTenantId, fetchColaboradores, fetchUsuarios])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!form.nome.trim()) {
      setSubmitError("Nome e obrigatorio.")
      return
    }
    if (!form.cpf.trim()) {
      setSubmitError("CPF e obrigatorio.")
      return
    }
    const cpfErr = cpfError(form.cpf)
    if (cpfErr) {
      setSubmitError(cpfErr)
      return
    }
    const telErr = phoneError(form.telefone)
    if (telErr) {
      setSubmitError(telErr)
      return
    }
    const telSecErr = optionalPhoneError(form.telefone_secundario)
    if (telSecErr) {
      setSubmitError(telSecErr)
      return
    }
    const cepErr = cepError(form.cep)
    if (cepErr) {
      setSubmitError(cepErr)
      return
    }
    if (!form.cargo.trim()) {
      setSubmitError("Cargo e obrigatorio.")
      return
    }
    
    setSubmitError(null)
    
    const payload = {
      nome: form.nome.trim(),
      cpf: onlyDigits(form.cpf),
      telefone: onlyDigits(form.telefone),
      telefone_secundario: onlyDigits(form.telefone_secundario) || null,
      email: form.email.trim() || null,
      cargo: form.cargo.trim(),
      departamento: form.departamento,
      data_admissao: form.data_admissao,
      salario: form.salario ? parseFloat(form.salario) : null,
      cidade: form.cidade.trim() || null,
      estado: form.estado || null,
      bairro: form.bairro.trim() || null,
      logradouro: form.logradouro.trim() || null,
      numero: form.numero.trim() || null,
      cep: onlyDigits(form.cep) || null,
      usuario_id: form.usuario_id || null,
    }

    try {
      const url = editColaborador 
        ? `${apiBase}/api/colaboradores/${editColaborador.id}`
        : `${apiBase}/api/colaboradores/`
      const res = await apiFetch(url, {
        method: editColaborador ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setEditColaborador(null)
        resetForm()
        fetchColaboradores()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const resetForm = () => {
    setForm({
      nome: "", cpf: "", telefone: "", telefone_secundario: "", email: "",
      cargo: "", departamento: "Producao", data_admissao: todayISODate(),
      salario: "", cidade: "", estado: "", bairro: "", logradouro: "", numero: "", cep: "",
      usuario_id: "",
    })
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este colaborador?")) return
    try {
      await apiFetch(`${apiBase}/api/colaboradores/${id}`, { method: "DELETE" })
      fetchColaboradores()
    } catch (e) {
      console.error(e)
    }
  }

  const handleToggleStatus = async (colaborador: Colaborador) => {
    const novoStatus = colaborador.status === "ATIVO" ? "INATIVO" : "ATIVO"
    try {
      await apiFetch(`${apiBase}/api/colaboradores/${colaborador.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: novoStatus }),
      })
      fetchColaboradores()
    } catch (e) {
      console.error(e)
    }
  }

  const handleUnlinkUsuario = async (colaborador: Colaborador) => {
    if (!confirm("Desvincular usuario deste colaborador?")) return
    try {
      await apiFetch(`${apiBase}/api/colaboradores/${colaborador.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario_id: null }),
      })
      fetchColaboradores()
    } catch (e) {
      console.error(e)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatCPF = (cpf: string) => maskCpf(cpf)

  const filteredColaboradores = colaboradores.filter(c => 
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cpf.includes(searchTerm.replace(/\D/g, '')) ||
    c.cargo.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const usuariosDisponiveis = usuarios.filter(u => 
    !colaboradores.some(c => c.usuario_id === u.id && c.id !== editColaborador?.id)
  )

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Colaboradores</h1>
          <p className="text-muted-foreground text-sm">Gerencie funcionarios e vincule a usuarios</p>
        </div>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditColaborador(null)
          resetForm()
        }}>
          <Plus className="h-4 w-4 mr-2" /> Novo Colaborador
        </Button>
      </div>

      <SetupModuleEmptyCta
        module="colaboradores"
        label="Colaboradores"
        href="/colaboradores"
        onAdd={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditColaborador(null)
          resetForm()
        }}
      />

      <div>
        <Input
          placeholder="Buscar por nome, CPF ou cargo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setEditColaborador(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editColaborador ? 'Editar Colaborador' : 'Novo Colaborador'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {editColaborador ? 'Editar' : 'Novo'} Colaborador
            </h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} maxLength={MASK_MAX_LENGTH.nome} required />
                </div>
                <div className="space-y-2">
                  <Label>CPF *</Label>
                  <Input
                    value={form.cpf}
                    onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={MASK_MAX_LENGTH.cpf}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={MASK_MAX_LENGTH.email} />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={MASK_MAX_LENGTH.phone}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone Secundario</Label>
                  <Input
                    value={form.telefone_secundario}
                    onChange={(e) => setForm({ ...form, telefone_secundario: maskPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={MASK_MAX_LENGTH.phone}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cargo *</Label>
                  <Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="Ex: Operador" maxLength={MASK_MAX_LENGTH.cargo} required />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {CARGOS_COMUNS.slice(0, 4).map(c => (
                      <Button key={c} type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setForm({ ...form, cargo: c })}>
                        {c}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Departamento</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}>
                    {DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Data Admissao</Label>
                  <Input type="date" value={form.data_admissao} onChange={(e) => setForm({ ...form, data_admissao: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Salario</Label>
                  <Input type="number" step="0.01" value={form.salario} onChange={(e) => setForm({ ...form, salario: e.target.value })} placeholder="0,00" />
                </div>
                <div className="space-y-2">
                  <Label>Vincular Usuario</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={form.usuario_id} onChange={(e) => setForm({ ...form, usuario_id: e.target.value })}>
                    <option value="">Sem usuario vinculado</option>
                    {usuariosDisponiveis.map(u => (
                      <option key={u.id} value={u.id}>{u.email} ({u.nome})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-base font-semibold">Endereco</Label>
                <div className="grid gap-4 md:grid-cols-4 mt-3">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input
                      value={form.cep}
                      onChange={(e) => setForm({ ...form, cep: maskCep(e.target.value) })}
                      placeholder="00000-000"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={MASK_MAX_LENGTH.cep}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="">Selecione</option>
                      {ESTADOS_BRASIL.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} maxLength={MASK_MAX_LENGTH.cidade} />
                  </div>
                  <div className="space-y-2">
                    <Label>Bairro</Label>
                    <Input value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })} maxLength={MASK_MAX_LENGTH.bairro} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-4 mt-4">
                  <div className="space-y-2 md:col-span-3">
                    <Label>Logradouro</Label>
                    <Input value={form.logradouro} onChange={(e) => setForm({ ...form, logradouro: e.target.value })} placeholder="Rua, Avenida..." maxLength={MASK_MAX_LENGTH.logradouro} />
                  </div>
                  <div className="space-y-2">
                    <Label>Numero</Label>
                    <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: maskNumero(e.target.value) })} maxLength={MASK_MAX_LENGTH.numero} />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setEditColaborador(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredColaboradores.map((c) => (
          <Card key={c.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center gap-2">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <span>{c.nome}</span>
                </div>
                <Badge variant={c.status === "ATIVO" ? "default" : "secondary"}>{c.status}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 text-sm">
                <p className="text-muted-foreground">CPF: {formatCPF(c.cpf)}</p>
                <p className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  {maskPhone(c.telefone)}
                </p>
                <p><strong>Cargo:</strong> {c.cargo}</p>
                <p className="text-muted-foreground">{c.departamento}</p>
                {c.salario && (
                  <p className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                    {formatCurrency(c.salario)}
                  </p>
                )}
                <p className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Admissão: {formatBRDate(c.data_admissao)}
                </p>
                {c.cidade && c.estado && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {c.cidade} - {c.estado}
                  </p>
                )}
                {c.usuario_id && c.usuario_email && (
                  <div className="flex items-center gap-1 text-green-600">
                    <Link2 className="h-3 w-3" />
                    <span className="text-xs">Vinculado: {c.usuario_email}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => {
                  setEditColaborador(c)
                  setForm({
                    nome: c.nome,
                    cpf: maskCpf(c.cpf),
                    telefone: maskPhone(c.telefone),
                    telefone_secundario: c.telefone_secundario ? maskPhone(c.telefone_secundario) : "",
                    email: c.email || "",
                    cargo: c.cargo,
                    departamento: c.departamento,
                    data_admissao: c.data_admissao,
                    salario: c.salario?.toString() || "",
                    cidade: c.cidade || "",
                    estado: c.estado || "",
                    bairro: c.bairro || "",
                    logradouro: c.logradouro || "",
                    numero: maskNumero(c.numero || ""),
                    cep: c.cep ? maskCep(c.cep) : "",
                    usuario_id: c.usuario_id || "",
                  })
                  setShowForm(true)
                }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {c.usuario_id && (
                  <Button size="sm" variant="outline" onClick={() => handleUnlinkUsuario(c)}>
                    <Unlink className="h-4 w-4" />
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => handleToggleStatus(c)}>
                  {c.status === "ATIVO" ? "Desativar" : "Ativar"}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredColaboradores.length === 0 && (
        <div className="py-8 text-center text-muted-foreground">
          <p>{searchTerm ? "Nenhum colaborador encontrado." : "Cadastre colaboradores para organizar a equipe da granja."}</p>
          {!searchTerm && <Button variant="outline" className="mt-3" onClick={() => { setSubmitError(null); setShowForm(true); setEditColaborador(null); resetForm() }}>Novo Colaborador</Button>}
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/colaboradores')({
  component: ColaboradoresPage,
})
